import React, { useEffect, useState, useContext } from 'react';
import Modal from 'react-modal';
import { SubscriptionPlan } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import BoxContentSectionTampon from '@components/dashboard/BoxContentSectionTampon';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import useDesktop from '@components/shared/useDesktop';
import { BoxState, default12TamponConfiguration, default18TamponConfiguration, Prices, TamponType } from '@contracts';
import CloseIcon from '@images/account/icon-x.svg';
import IconTampon from '@images/icon-tampon-clear.svg';
import SelectedRadio from '@images/selected-radio.svg';
import Tick from '@images/tick.svg';
import UnselectedRadio from '@images/unselected-radio.svg';
import { getTamponConfigAndCounts } from '@utils/product';
import { getCurrentTamponsCount } from '@utils/tampons';

enum TabType {
  preselectBox = 'preselectBox',
  customiseBox = 'customiseBox'
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '500px',
    height: '780px',
    maxWidth: '90vw',
    maxHeight: '92vh',
    padding: '0',
    border: '0',
    borderRadius: '0',
    background: '#FFFBE7'
  },
  overlay: {
    background: `${Colors.navyBlue}25`
  }
};

interface ModalProps {
  modalOpen: boolean;
  closeModal: () => void;
  defaultValue: { label: string; value: any };
  boxState: BoxState;
  prices?: Prices;
  subscriptions: SubscriptionPlan[];
  setBoxStateOuter: (value: BoxState) => void;
  handleOptionSelect: (value: BoxState) => void;
  handlePresetBoxesUpdate: (value: BoxState) => void;
  hasSelectedIndicator: boolean;
  presetBoxes?: any;
}

const TamponCustomiseBoxModal: React.FC<ModalProps> = ({
  modalOpen,
  closeModal,
  defaultValue,
  boxState,
  prices,
  subscriptions,
  setBoxStateOuter,
  handleOptionSelect,
  handlePresetBoxesUpdate,
  hasSelectedIndicator,
  presetBoxes
}) => {
  const { t } = useTranslation();
  const isDesktop = useDesktop(768);
  const { state } = useContext(Context);
  const { currency, country } = state;
  const [modalBoxState, setModalBoxState] = useState<BoxState>(boxState);
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState(defaultValue ?? presetBoxes[modalBoxState.boxSize.toString()][0]);
  const [selectedTab, setSelectedTab] = useState(TabType.customiseBox);
  const [isFull, setIsFull] = useState(false);
  const { boxSize, nakedsuper, nakedregular, cbdregular, cbdsuper } = modalBoxState;
  const sumOfTamps = nakedregular + nakedsuper + cbdregular + cbdsuper;
  const modalTitle = t('components.customise-your-box').toUpperCase();

  useEffect(() => {
    checkBoxIsFull();
    if (sumOfTamps === boxSize) {
      setIsFull(true);
    } else setIsFull(false);
  }, [modalBoxState]);

  const checkBoxIsFull = () => {
    const tamponsCount = getCurrentTamponsCount(modalBoxState);
    if (tamponsCount === boxSize) {
      setIsFull(true);
    } else {
      setIsFull(false);
    }
  };

  const onOptionSelect = (option: any) => {
    setSelected(option);
    setOpen(false);
    const { config } = getTamponConfigAndCounts(option.value);
    setModalBoxState({ ...config, boxSize });
  };

  function calculateMinBoxPrice(config: any) {
    const nakedTamponsSum = config.nakedregular + config.nakedsuper;
    const cbdTamponsSum = config.cbdregular + config.cbdsuper;

    const totalPrice = nakedTamponsSum * prices?.nakedTamponPrice + cbdTamponsSum * prices?.cbdTamponPrice;
    const maxPlan = subscriptions && subscriptions[subscriptions.length - 1];
    const discountPercent = maxPlan ? maxPlan.discountPercent[country] ?? maxPlan.discountPercent.default : 0;
    const discountedPrice = totalPrice - (totalPrice * discountPercent) / 100;

    return Math.round(discountedPrice * 20) / 20;
  }

  function getBoxMinPrice(option: any) {
    const { config } = getTamponConfigAndCounts(option.value);
    return calculateMinBoxPrice(config).toFixed(2);
  }

  function renderOptions(option: any) {
    const minBoxPrice = getBoxMinPrice(option);
    const priceParts = minBoxPrice.split('.');
    return (
      <Option key={option.label} onClick={() => onOptionSelect(option)} $isSelected={option.value === selected?.value}>
        <RowWrap>
          {hasSelectedIndicator && (option.value === selected?.value ? <SelectedRadio /> : <UnselectedRadio />)}
          <OptionText>{t(option.label)}</OptionText>
          {option.img && <img src={option.img} />}
          {children}
        </RowWrap>
        <div>
          <LightText>From</LightText>
          <RowWrap>
            <PriceText>
              {currency.symbol}
              {priceParts[0]}
            </PriceText>
            {minBoxPrice && <PriceText style={{ fontSize: '12px' }}>{priceParts[1]}</PriceText>}
          </RowWrap>
        </div>
      </Option>
    );
  }

  const updateVariants = (key: string, value: number) => {
    setModalBoxState((prevState) => ({
      ...prevState,
      [key]: value
    }));
  };

  function renderCustomiseBox() {
    return (
      <BoxContentsWrap>
        <BoxContentSectionTampon
          variant={TamponType.CBD_SUPER}
          count={cbdsuper ?? 0}
          updateVariants={updateVariants}
          showTamponIcon={false}
          tamponsFull={isFull}
        />
        <BoxContentSectionTampon
          variant={TamponType.CBD_REGULAR}
          count={cbdregular ?? 0}
          updateVariants={updateVariants}
          showTamponIcon={false}
          tamponsFull={isFull}
        />
        <BoxContentSectionTampon
          variant={TamponType.NAKED_SUPER}
          count={nakedsuper ?? 0}
          updateVariants={updateVariants}
          showTamponIcon={false}
          tamponsFull={isFull}
        />
        <BoxContentSectionTampon
          variant={TamponType.NAKED_REGULAR}
          count={nakedregular ?? 0}
          updateVariants={updateVariants}
          showTamponIcon={false}
          tamponsFull={isFull}
        />
      </BoxContentsWrap>
    );
  }

  const children = (
    <ModalContainer>
      <ModalHeader>
        <h2>{t('components.customise-your-box')}</h2>
        <button onClick={closeModal}>
          <CloseIcon />
        </button>
      </ModalHeader>
      <PresetBoxWrap>
        <SizeSelectWrap>
          <BoxWrapper
            $isSelected={boxSize === 12}
            onClick={() => {
              setSelected(presetBoxes[12][0]);
              setModalBoxState(default12TamponConfiguration);
            }}
          >
            {!isDesktop && <TextWrap>12</TextWrap>}
            <IconTampon />
            {isDesktop && <TextWrap>{t('components.tampon-boxes.12-tampons')}</TextWrap>}
          </BoxWrapper>
          <BoxWrapper
            $isSelected={boxSize === 18}
            onClick={() => {
              setSelected(presetBoxes[18][0]);
              setModalBoxState(default18TamponConfiguration);
            }}
          >
            {!isDesktop && <TextWrap>18</TextWrap>}
            <IconTampon />
            {isDesktop && <TextWrap>{t('components.tampon-boxes.18-tampons')}</TextWrap>}
          </BoxWrapper>
        </SizeSelectWrap>
      </PresetBoxWrap>
      <div>
        <BoxOptionsWrap>
          <BoxOption $isSelected={selectedTab === TabType.preselectBox} onClick={() => setSelectedTab(TabType.preselectBox)}>
            {selectedTab === TabType.preselectBox && <TickStyled />}
            <p>{t('components.preset-boxes')}</p>
          </BoxOption>
          <BoxOption $isSelected={selectedTab === TabType.customiseBox} onClick={() => setSelectedTab(TabType.customiseBox)}>
            {selectedTab === TabType.customiseBox && <TickStyled />}
            <p>{t('components.choose-your-own')}</p>
          </BoxOption>
        </BoxOptionsWrap>
      </div>
      {selectedTab === TabType.preselectBox && (
        <OptionWrap>
          <Options $open={open} $options={presetBoxes?.length ?? 0}>
            {presetBoxes && presetBoxes[modalBoxState.boxSize.toString()].map((box) => renderOptions(box))}
          </Options>
        </OptionWrap>
      )}
      {selectedTab === TabType.customiseBox && renderCustomiseBox()}
      <ConfirmWrap>
        <ButtonConfirm
          disabled={sumOfTamps < boxSize}
          onClick={() => {
            setBoxStateOuter(modalBoxState);
            if (selectedTab === TabType.customiseBox) {
              handlePresetBoxesUpdate(modalBoxState);
            } else {
              handleOptionSelect(selected);
            }

            closeModal();
          }}
        >
          {t('components.confirm-selection')}
        </ButtonConfirm>
      </ConfirmWrap>
    </ModalContainer>
  );

  return (
    <Modal
      isOpen={modalOpen}
      onAfterOpen={() => null}
      onRequestClose={closeModal}
      style={customStyles}
      closeTimeoutMS={250}
      contentLabel={modalTitle}
    >
      {children}
    </Modal>
  );
};

export default TamponCustomiseBoxModal;

const BoxWrapper = styled.div<SelectButtonProps>`
  width: 186px;
  height: 67px;
  display: flex;
  flex-direction: row;
  place-content: center;
  align-items: center;
  gap: 10px;
  border: 1px solid ${Colors.paleGold};
  background-color: ${(props) => (props.$isSelected ? Colors.ecru : 'transparent')};

  & > div {
    border-color: ${(props) => (props.$isSelected ? Colors.paleGold : 'transparent')};
  }

  svg {
    width: 15px;
    height: 27px;

    path {
      fill: ${Colors.navyBlue};
    }
  }

  @media (max-width: 768px) {
    width: 103px;
    height: 57px;
  }
`;

const ModalHeader = styled.div`
  padding: 32px;
  background: #c5e3c1;
  position: sticky;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;

  h2 {
    font-family: 'BrownStd';
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 1px;
    text-align: center;
    text-transform: uppercase;
    color: #003b1b;

    @media (max-width: 1024px) {
      text-align: left;
    }
  }

  button {
    background: transparent;
    position: absolute;
    right: 30px;
    top: 50%;
    border: 0;
    padding: 0;
    height: 20px;
    width: 20px;
    margin-top: -10px;
    outline: none;
    cursor: pointer;

    svg {
      width: 100%;
      height: 100%;
    }

    &:hover {
      opacity: 0.5;
    }
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 500px;
`;

const BoxOptionsWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  border-bottom: 1px solid ${Colors.ecru};

  p {
    font-family: BrownStd;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 150%;

    @media (max-width: 768px) {
      font-weight: 700;
      font-size: 14px;
    }
  }
`;

const BoxOption = styled.div<SelectButtonProps>`
  display: flex;
  flex-direction: row;
  padding: 4px 25px;
  gap: 8px;
  border-bottom: ${(props) => (props.$isSelected ? `3px solid ${Colors.gold}` : `3px solid ${Colors.ecru}`)};
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 4px 8px;
  }
`;

const OptionWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding-inline: 30px;

  & > div {
    margin: 20px 0;
    flex-direction: column;

    .image-title-wrap {
      margin-bottom: 20px;

      & > div {
        display: flex;
        gap: 5px;
        margin-right: 0;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      margin: -2px 0 0 0;
      border-left-width: 0;
      border-right-width: 0;
      flex-direction: row;

      .image-title-wrap {
        margin-bottom: 0;
      }
    }
  }

  @media (max-width: 768px) {
    padding-inline: 0;
  }
`;

const TextWrap = styled.div`
  font-family: BrownStd;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;

  @media (max-width: 768px) {
    font-weight: 700;
    font-size: 18px;
    line-height: 120%;
  }
`;

const ConfirmWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: end;
  background: ${Colors.ecru}80;
  border-top: 1px solid ${Colors.ecru};
  padding: 20px 30px;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;

    & > button {
      width: 100%;
    }
  }
`;

interface SelectButtonProps {
  $isSelected: boolean;
  value?: string | number;
  onClick?: () => void;
}

const SizeSelectWrap = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 20px;
    gap: 20px;
  }

  @media (max-width: 410px) {
    justify-content: space-between;
    gap: 0;
  }
`;

const PresetBoxWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 30px;
  gap: 20px;

  & > div {
    cursor: pointer;

    @media (max-width: 768px) {
      flex-basis: calc(50% - 20px);
    }

    @media (max-width: 660px) {
      flex-basis: 100%;
    }
  }
`;

interface IOptions {
  $open: boolean;
  $options?: number;
  $hasHeading?: boolean;
}

const Options = styled.div<IOptions>`
  width: 100%;
  background: ${Colors.cream};
  transition: height 0.2s ease-out;
  border: 1px solid ${Colors.paleGold};
  z-index: 2;
  top: 100%;

  ${({ $open, $options }) => {
    if ($open) {
      return `
        display: flex;
        flex-direction: column;
        min-height: ${49 * ($options < 4 ? $options : 4)}px;
        max-height: ${49 * 6}px;
        border-bottom: 1px solid ${Colors.paleGold};
      `;
    }
    return '';
  }};
`;

const RowWrap = styled.div`
  display: flex;
  flex-direction: row;
`;

interface IOption {
  $isSelected: boolean;
}

const Option = styled.div<IOption>`
  width: 100%;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 0.8;

  &:hover {
    cursor: pointer;
    opacity: 1;
  }

  svg {
    margin-right: 15px;
  }

  border-bottom: 1px solid ${Colors.paleGold};

  &:last-child {
    border-bottom-width: 0;
  }

  ${({ $isSelected }) => {
    if ($isSelected) {
      return `
        background: #FCF3DF
      `;
    }
    return '';
  }};
`;

const OptionText = styled(P)<SelectButtonProps>`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  margin-right: 10px;
  color: ${Colors.navyBlue};

  &.selected {
    font-weight: 700;
  }

  span {
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    max-width: 150px;
  }
`;

const BoxContentsWrap = styled.div`
  padding: 27.8px 44px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const LightText = styled(OptionText)`
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  opacity: 0.5;
  width: 100%;
  text-align: center;
`;

const PriceText = styled.div`
  font-family: Domaine;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;
  color: ${Colors.navyBlue};
`;

const TickStyled = styled(Tick)`
  width: 12px;
  height: 12px;
  align-self: center;

  path {
    fill: ${Colors.navyBlue};
  }
`;

const ButtonConfirm = styled(Button)`
  align-self: end;
`;
